import {
  ColDef,
  RowClassParams,
  RowClickedEvent,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import logo from '@assets/logo.svg';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { Notification } from '@components';
import { useNavigate } from '@solidjs/router';
import { PendingApprovals } from '@store/Global/types';
import { sidebarDataStore } from '@store/index';
import { dateFormatter } from '@store/loadboard';
import { formatDateString } from '@utils/dateFormat';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { createSignal, Setter } from 'solid-js';
import { render } from 'solid-js/web';
import '../../Grid/AgGridStyles.css';

const agGridCellStyle = {
  padding: '5px 5px',
  'word-break': 'break-word',
  'white-space': 'normal',
  'line-height': '1',
  'align-items': 'center !important',
};

const commonColumns: ColDef[] = [
  {
    field: 'order',
    headerName: 'Order',
    minWidth: 90,
    maxWidth: 110,
    filter: false,
    cellStyle: agGridCellStyle,
  },
  {
    field: 'setBy',
    headerName: 'Set By',
    filter: 'agTextColumnFilter',
    minWidth: 90,
    maxWidth: 150,
    autoHeight: true,
    cellStyle: agGridCellStyle,
  },
  {
    field: 'setDate',
    headerName: 'Date',
    filter: 'agDateColumnFilter',
    filterParams: {
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    maxWidth: 110,
    minWidth: 110,
    valueFormatter: dateFormatter,
  },
  {
    field: 'description',
    headerName: 'Details',
    filter: 'agTextColumnFilter',
    minWidth: 150,
    maxWidth: 350,
    autoHeight: true,
    cellStyle: agGridCellStyle,
  },
  {
    field: 'description',
    headerName: 'Last Comment',
    filter: 'agTextColumnFilter',
    minWidth: 150,
    autoHeight: true,
    cellStyle: agGridCellStyle,
    cellRenderer: (params: { data: PendingApprovals }) => {
      const container = document.createElement('div');
      const [Component] = createSignal(Description(params.data));
      render(() => <Component />, container);
      return container;
    },
  },
];

export const Description = (data: PendingApprovals) => {
  return (
    <>
      <span>{`${data.lastComment ?? ''}`}</span>
      <br />
      <span>
        {`${Boolean(data.lastCommentBy) ? ` - ${data.lastCommentBy}` : ''} ${
          Boolean(data.lastCommentDate)
            ? `(${formatDateString(
                data.lastCommentDate,
                'MMM dd yyyy hh:mm a',
              )})`
            : ''
        }`}
      </span>
    </>
  );
};

const ApprovalsWarningGrid = (props: { setActiveTab: Setter<number> }) => {
  const navigate = useNavigate();
  let gridRef: AgGridSolidRef | undefined;
  const imageTag = `<img src=${logo} alt="" />`;

  const getRowStatusStyle = (params: RowClassParams) => {
    const data: PendingApprovals = params.data;
    // Start with empty base style
    const baseStyle: Record<string, string> = {
      background: '',
      color: '',
      borderBottom: '1px solid #DDE2EB',
    };

    // Check conditions independently and apply styles
    if (data.overdue) {
      baseStyle.color = '#a94442'; // Red text for overdue
    }

    if (data.pastDue) {
      baseStyle.background = '#F0D9D9'; // Pink background for past due
    }

    return baseStyle;
  };
  return (
    <>
      <Notification
        sxProps="bg-white! text-sm!"
        type="info"
        text={
          <span class="text-sm">
            These Loads have been sitting in your approval queue for awhile.
            Please address them so that we can proceed with Billing
          </span>
        }
      />
      <Box class="ag-theme-alpine flex flex-col flex-1! h-[82%]! ">
        <AgGridSolid
          ref={gridRef}
          columnDefs={commonColumns}
          rowData={sidebarDataStore.approvals.data}
          gridOptions={{
            defaultColDef: {
              flex: 1,
              filter: false,
            },
          }}
          class={'ag-approval-grid'}
          getRowStyle={getRowStatusStyle}
          overlayLoadingTemplate={imageTag}
          noRowsOverlayComponent={imageTag}
          onRowClicked={(e: RowClickedEvent) => {
            const orderId = (e.data as { order: number }).order;
            if (routeOrderDetails(orderId)) {
              props.setActiveTab(-1);
              navigate(`/order/details/${orderId}`);
            }
          }}
        />
      </Box>
    </>
  );
};

export default ApprovalsWarningGrid;
